import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    IconButton,
    useToast,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    FormControl,
    FormLabel,
    Input,
    VStack,
    Badge,
    useDisclosure,
    Switch
} from '@chakra-ui/react';
import { DeleteIcon, EditIcon, AddIcon } from '@chakra-ui/icons';
import { useAuth } from '../contexts/AuthContext';
import { fetchWithAuth } from '../utils/fetchWithAuth';


function UserManagement() {
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        full_name: '',
        password: '',
        is_active: true
    });
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { token } = useAuth();
    const toast = useToast();

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await fetchWithAuth('/api/auth/users', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setUsers(data);
            }
        } catch (error) {
            console.error('Failed to fetch users:', error);
        }
    };

    const handleCreate = () => {
        setSelectedUser(null);
        setFormData({
            username: '',
            email: '',
            full_name: '',
            password: '',
            is_active: true
        });
        onOpen();
    };

    const handleEdit = (user) => {
        setSelectedUser(user);
        setFormData({
            username: user.username,
            email: user.email,
            full_name: user.full_name || '',
            password: '',
            is_active: user.is_active
        });
        onOpen();
    };

    const handleSubmit = async () => {
        try {
            const url = selectedUser
                ? `/api/auth/users/${selectedUser.id}`
                : '/api/auth/users';
            
            const method = selectedUser ? 'PATCH' : 'POST';
            
            const body = selectedUser
                ? {
                    email: formData.email,
                    full_name: formData.full_name,
                    is_active: formData.is_active,
                    ...(formData.password && { password: formData.password })
                  }
                : formData;

            const response = await fetchWithAuth(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(body)
            });

            if (response.ok) {
                toast({
                    title: `User ${selectedUser ? 'updated' : 'created'} successfully`,
                    status: 'success',
                    duration: 3000,
                });
                fetchUsers();
                onClose();
            } else {
                const error = await response.json();
                throw new Error(error.detail);
            }
        } catch (error) {
            toast({
                title: 'Error',
                description: error.message,
                status: 'error',
                duration: 5000,
            });
        }
    };

    const handleDelete = async (userId) => {
        if (!window.confirm('Are you sure you want to delete this user?')) return;

        try {
            const response = await fetchWithAuth(`/api/auth/users/${userId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                toast({
                    title: 'User deleted successfully',
                    status: 'success',
                    duration: 3000,
                });
                fetchUsers();
            }
        } catch (error) {
            toast({
                title: 'Error deleting user',
                status: 'error',
                duration: 5000,
            });
        }
    };

    return (
        <Box p={6}>
            <Button
                leftIcon={<AddIcon />}
                colorScheme="brand"
                onClick={handleCreate}
                mb={4}
            >
                Add User
            </Button>

            <Table variant="simple">
                <Thead>
                    <Tr>
                        <Th>Username</Th>
                        <Th>Email</Th>
                        <Th>Full Name</Th>
                        <Th>Status</Th>
                        <Th>Admin</Th>
                        <Th>Actions</Th>
                    </Tr>
                </Thead>
                <Tbody>
                    {users.map(user => (
                        <Tr key={user.id}>
                            <Td>{user.username}</Td>
                            <Td>{user.email}</Td>
                            <Td>{user.full_name || '-'}</Td>
                            <Td>
                                <Badge colorScheme={user.is_active ? 'green' : 'red'}>
                                    {user.is_active ? 'Active' : 'Inactive'}
                                </Badge>
                            </Td>
                            <Td>
                                <Badge colorScheme={user.is_admin ? 'purple' : 'gray'}>
                                    {user.is_admin ? 'Admin' : 'User'}
                                </Badge>
                            </Td>
                            <Td>
                                <IconButton
                                    icon={<EditIcon />}
                                    size="sm"
                                    mr={2}
                                    onClick={() => handleEdit(user)}
                                />
                                <IconButton
                                    icon={<DeleteIcon />}
                                    size="sm"
                                    colorScheme="red"
                                    onClick={() => handleDelete(user.id)}
                                />
                            </Td>
                        </Tr>
                    ))}
                </Tbody>
            </Table>

            <Modal isOpen={isOpen} onClose={onClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        {selectedUser ? 'Edit User' : 'Create User'}
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={4}>
                            <FormControl isRequired={!selectedUser}>
                                <FormLabel>Username</FormLabel>
                                <Input
                                    value={formData.username}
                                    onChange={(e) => setFormData({...formData, username: e.target.value})}
                                    isDisabled={!!selectedUser}
                                />
                            </FormControl>

                            <FormControl isRequired>
                                <FormLabel>Email</FormLabel>
                                <Input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel>Full Name</FormLabel>
                                <Input
                                    value={formData.full_name}
                                    onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                                />
                            </FormControl>

                            <FormControl isRequired={!selectedUser}>
                                <FormLabel>
                                    Password {selectedUser && '(leave blank to keep current)'}
                                </FormLabel>
                                <Input
                                    type="password"
                                    value={formData.password}
                                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                                    placeholder="Min 8 chars, 1 uppercase, 1 number"
                                />
                            </FormControl>

                            {selectedUser && (
                                <FormControl display="flex" alignItems="center">
                                    <FormLabel mb="0">Active</FormLabel>
                                    <Switch
                                        isChecked={formData.is_active}
                                        onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                                    />
                                </FormControl>
                            )}
                        </VStack>
                    </ModalBody>

                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onClose}>
                            Cancel
                        </Button>
                        <Button colorScheme="brand" onClick={handleSubmit}>
                            {selectedUser ? 'Update' : 'Create'}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
}

export default UserManagement;